import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

export const outsideAgencySlice = createSlice({
    name: 'outsideAgency',
    initialState: {
        data: [],
        statuses: [
            { name: 'Available', color: '#dff0d8', icon: '', next: 'Dispatched', action_name: 'Clear', group: 'Available', sequence: 1 },
            { name: 'Dispatched', color: '#f2d9f7', icon: '', next: 'Responding', action_name: 'Dispatch', group: 'Assigned', sequence: 1 },
            { name: 'Responding', color: '#d9edf7', icon: '', next: 'On Scene', action_name: 'Responding', group: 'Assigned', sequence: 2 },
            { name: 'On Scene', color: '#00800b', icon: '', next: 'Available', action_name: 'On Scene', group: 'Assigned', sequence: 3 },
            { name: 'Transporting', color: '#2087ba', icon: '', next: 'Destination', action_name: 'Transporting', group: 'Assigned', sequence: 4 },
            { name: 'Destination', color: '#ebebeb', icon: '', next: 'Available', action_name: 'Destination', group: 'Assigned', sequence: 5 }
        ]
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const agency of ensureArray(action.payload)) {
                const idx = state.data.findIndex(a => a.callsign === agency.callsign);
                if (idx >= 0) {
                    state.data.splice(idx, 1, agency);
                } else {
                    state.data.push(agency);
                }
            }
        },
        remove: (state, action) => {
            for (const agency of ensureArray(action.payload)) {
                let idx = state.data.findIndex(a => a.callsign === agency.callsign);
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        },
        addOrUpdateJob: (state, action) => {
            let agency = state.data.find(a => a.callsign === action.payload.callsign);
            if (agency) {
                if (agency.jobs?.length > 0) {
                    let idx = agency.jobs.findIndex(j => j.jobid.toString() === action.payload.jobid.toString());
                    if (idx >= 0) {
                        agency.jobs.splice(idx, 1, action.payload);
                    } else {
                        agency.jobs.push(action.payload);
                    }
                } else {
                    agency.jobs = [action.payload];
                }
            }
        },
        removeJob: (state, action) => {
            let agency = state.data.find(a => a.callsign === action.payload.callsign);
            if (agency?.jobs?.length > 0) {
                agency.jobs = agency.jobs.filter(j => j.jobid.toString() !== action.payload.jobid.toString());
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => outsideAgencySlice.getInitialState());
    }
});

// #region network operations
const fetchOutsideAgencyStatuses = network => {};
const fetchAgencies = network => network.get('/api/v1/outsideAgencies', outsideAgencySlice.actions.set);
export const createAgency = (network, agency) => network.post('/api/v1/outsideAgency', agency, outsideAgencySlice.actions.addOrUpdate);
export const updateAgency = (network, agency) => network.put('/api/v1/outsideAgency', agency, outsideAgencySlice.actions.addOrUpdate);
export const removeAgency = (network, agency) => network.delete('/api/v1/outsideAgency/' + encodeURIComponent(agency.callsign), outsideAgencySlice.actions.remove);
export const addAgencyJob = (network, agencyJob) => network.post('/api/v1/outsideAgencyJob', agencyJob, outsideAgencySlice.actions.addOrUpdateJob);
export const updateAgencyJob = (network, agencyJob) => network.put('/api/v1/outsideAgencyJob', agencyJob, outsideAgencySlice.actions.addOrUpdateJob);
export const removeAgencyJob = (network, agencyJob) => network.delete(`/api/v1/outsideAgencyJob/${encodeURIComponent(agencyJob.callsign)}/${encodeURIComponent(agencyJob.jobid)}`, outsideAgencySlice.actions.removeJob);
// #endregion

// #region permission checks
export const canUpdateAgency = (credentials, agency) => credentials.access.hasFull('administrator');
export const canCreateAgency = (credentials, agency) => credentials.access.hasFull('administrator');
export const canRemoveAgency = (credentials, agency) => credentials.access.hasFull('administrator');

// #region selectors
const selectOutsideAgencyStatuses = state => state.outsideAgency.statuses;
const selectOutsideAgencies = state => state.outsideAgency.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectOutsideAgencyByCallsign = createSelector([selectOutsideAgencies, (agencies, callsign) => callsign], (agencies, callsign) => agencies.find(oa => oa.callsign === callsign));
const selectOutsideAgency = callsign => state => selectOutsideAgencyByCallsign(state, callsign);

const selectOutsideAgencyByJobID = createSelector([selectOutsideAgencies, (agencies, jobid) => jobid], (agencies, jobid) => agencies.filter(oa => oa.jobs?.some(j => j.jobid.toString() === jobid?.toString())));
const selectJobOutsideAgencies = jobid => state => selectOutsideAgencyByJobID(state, jobid);
// #endregion

// #region listeners
const listen = {
    agencyCreated: (crew, { dispatch }) => dispatch(outsideAgencySlice.actions.addOrUpdate(crew)),
    agencyUpdated: (crew, { dispatch }) => dispatch(outsideAgencySlice.actions.addOrUpdate(crew)),
    agencyDeleted: (crew, { dispatch }) => dispatch(outsideAgencySlice.actions.remove(crew)),
    agencyJobAdded: (agencyJob, { dispatch }) => {
        delete agencyJob.jobid_internalid;
        delete agencyJob.type;
        dispatch(outsideAgencySlice.actions.addOrUpdateJob(agencyJob))
    },
    agencyJobUpdated: (agencyJob, { dispatch }) => {
        delete agencyJob.jobid_internalid;
        delete agencyJob.type;
        dispatch(outsideAgencySlice.actions.addOrUpdateJob(agencyJob))
    },
    agencyJobCleared: (agencyJob, { dispatch }) => {
        delete agencyJob.jobid_internalid;
        delete agencyJob.type;
        dispatch(outsideAgencySlice.actions.removeJob(agencyJob))
    }
}
// #endregion

export const [useOutsideAgencyStatuses] = dataHookFactory(selectOutsideAgencyStatuses, name => ({ run: fetchOutsideAgencyStatuses }));
export const [useOutsideAgencies] = dataHookFactory(selectOutsideAgencies, { run: fetchAgencies, listen });
export const [useOutsideAgency] = dataHookFactory(selectOutsideAgency, initMapFactory(callsign => fetchAgencies, listen));
export const [useJobOutsideAgencies] = dataHookFactory(selectJobOutsideAgencies, initMapFactory(jobid => fetchAgencies, listen));

const reducerDetails = { name: outsideAgencySlice.name, reducer: outsideAgencySlice.reducer };
export default reducerDetails;